/**
 * VectorHub job unwrap helpers for RestScript handlers.
 *
 * Row mappers (`toProduct`, `toParty`, …) live in `@syscolect/domain`
 * (`integration/legacy`) — re-exported here so existing handler imports keep working.
 */

export {
  toProduct,
  toParty,
  toInvoice,
  toInvoiceItem,
} from '@syscolect/domain'

/** Minimal VectorHub job shape returned by `POST /jobs` with `sync: true`. */
export interface VhResultMeta {
  rowCount?: number
  truncated?: boolean
  totalCount?: number
  offset?: number
  pageSize?: number
  hasMore?: boolean
  /** Fields dropped by VH whitelist (data.execute filter pushdown). */
  ignoredFilters?: string[]
  /** Sort field dropped by VH whitelist (data.execute caller sort — VH 0035). */
  ignoredSort?: string
}

export interface VhJobResult {
  data?: unknown
  meta?: VhResultMeta
}

export interface VhJob {
  status: string
  result?: VhJobResult
  error?: {
    code?: string
    message?: string
  }
}

export interface VhJobsResponse {
  job: VhJob
}

type LegacyRow = Record<string, unknown>

/**
 * Unwraps rows from a completed VectorHub sync job.
 * Expects `job.result.data` (QueryResult from `data.execute`); tolerates
 * one extra nesting level if the server persisted the full action envelope.
 */
export function extractRows(job: VhJob): LegacyRow[] {
  if (job.status !== 'completed') {
    const message =
      job.error?.message ??
      `VectorHub job failed with status "${job.status ?? 'unknown'}".`
    throw new Error(message)
  }

  const result = job.result
  if (!result) return []

  const direct = result.data
  if (Array.isArray(direct)) {
    return direct as LegacyRow[]
  }

  if (direct && typeof direct === 'object' && Array.isArray((direct as LegacyRow).data)) {
    return (direct as LegacyRow).data as LegacyRow[]
  }

  if (Array.isArray(result)) {
    return result as LegacyRow[]
  }

  return []
}

/**
 * Unwraps pagination meta from a completed VectorHub sync job.
 * Expects `job.result.meta` (QueryResult from `data.execute`); tolerates
 * one extra nesting level if the server persisted the full action envelope.
 */
export function extractMeta(job: VhJob): VhResultMeta {
  if (job.status !== 'completed') {
    const message =
      job.error?.message ??
      `VectorHub job failed with status "${job.status ?? 'unknown'}".`
    throw new Error(message)
  }

  const result = job.result
  if (!result) return {}

  const direct = result.meta
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct
  }

  const data = result.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const nested = (data as Record<string, unknown>).meta
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as VhResultMeta
    }
  }

  return {}
}
